import React, { useEffect, useRef, useState } from 'react';

import { ValueBox, ValueBoxGroupOptions, ValueStepper } from '@candu-ai/craft/ui';

interface StepperProps {
  property: 'top' | 'left' | 'right';
  label?: string;
  options?: { label: string; value: string }[];
  onPropertyChange?: (value: string) => void;
  isInGroup: ValueBoxGroupOptions;
  cssValue: string | undefined;
  setValue: (payload: Record<string, unknown>) => void;
}

export const BeaconPositionStepper = ({
  property,
  label,
  options,
  onPropertyChange,
  isInGroup,
  cssValue,
  setValue,
}: StepperProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const value = cssValue ? parseFloat(cssValue) : undefined;
  const defaultValue = 0;
  const displayValue = value || defaultValue;

  const [inputValue, setInputValue] = useState<number | undefined>(displayValue);

  const [inEditMode, setInEditMode] = useState(false);

  const onSetValue = (newValue?: number) => {
    const valueChanged = newValue !== inputValue;
    setInEditMode(true);

    if (valueChanged) {
      setInputValue(newValue);

      // only trigger change when there is no value or the value is within the accepted range
      if (typeof newValue === 'undefined') {
        if (property === 'right') {
          // when positioned relative to the right of the element, we cannot have it unset as it shows up in the wrong place
          setValue({ [property]: '0px' });
        } else {
          setValue({ [property]: undefined });
        }
      } else if (newValue >= -10000 && newValue <= 10000) {
        setValue({ [property]: `${newValue}px` });
      }
    }
  };

  useEffect(() => {
    // when value or type changes due to external factors (caller auto-adjusted value/type, viewport changed, node changed, etc.)
    // if we're not in edit mode, reflect value/type change in input
    if (!inEditMode) {
      if (displayValue !== inputValue) {
        setInputValue(displayValue);
      }
    }
  }, [displayValue, inEditMode]);

  return (
    <ValueBox
      label={label}
      options={options}
      onOptionChange={onPropertyChange}
      isInGroup={isInGroup}
    >
      <ValueStepper
        ref={inputRef}
        step={1}
        name={`beacon-position-${property}`}
        placeholder={!inEditMode ? 'unset' : undefined}
        valueSuffix="px"
        editable
        format="inline"
        minValue={-10000}
        maxValue={10000}
        onChange={onSetValue}
        onBlur={() => {
          setInEditMode(false);
        }}
        value={inputValue}
        onReset={
          typeof inputValue !== 'undefined' && inputValue !== defaultValue
            ? () => {
                setValue({ [property]: property === 'right' ? '0px' : undefined });
                setInputValue(defaultValue);
              }
            : undefined
        }
      />
    </ValueBox>
  );
};
